import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukWhen } from "@/lib/time";
import { Btn, Divider, Inp, Panel, St, Sw } from "@/components/portal/crm/kit";
import { ConfirmDialog, mono } from "./ConfirmDialog";

const ok = (s) => s >= 200 && s < 300;

/** Where every stage move is sent, signed, the moment it happens. */
export const WebhookPanel = ({ info, deliveries, onChange }) => {
  const hook = info?.webhook || {};
  const [f, setF] = useState({ url: "", enabled: false });
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [rotating, setRotating] = useState(false);
  useEffect(() => { setF({ url: hook.url || "", enabled: !!hook.enabled }); }, [hook.url, hook.enabled]);

  const run = async (fn, done) => {
    setBusy(true);
    try { const r = await fn(); if (done) toast.success(done(r)); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't go through"); }
    finally { setBusy(false); }
  };
  const save = () => run(() => api.put("/refer/api/webhook", f), () => (f.enabled ? "Saved — stage updates will go to that address" : "Saved — stage updates are off"));
  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/refer/api/webhook/test");
      if (r.data.ok) toast.success(`Test delivered — your system answered ${r.data.status}`);
      else toast.error(`Your system answered ${r.data.status || "nothing"} — check the address`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't go through"); }
    finally { setBusy(false); }
  };
  const rotate = () => { setRotating(false); run(() => api.post("/refer/api/webhook/secret"), () => "New signing secret — update it in your system"); };

  return (
    <Panel title="Stage updates to your system" testId="api-webhook-panel"
           sub="Every time a case moves, we POST it to your address, signed with your secret. You can also ask for any case's stage whenever you like.">
      <div style={{ display: "grid", gap: 14 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13.5, cursor: "pointer" }}>
          <Sw on={f.enabled} onClick={() => setF((s) => ({ ...s, enabled: !s.enabled }))} testId="api-webhook-enabled" label="Send stage updates to my system" />
          <span>Send stage updates to my system</span>
        </label>
        <Inp placeholder="https://your-system.com/connexis/updates" value={f.url} data-testid="api-webhook-url"
             onChange={(e) => setF((s) => ({ ...s, url: e.target.value }))} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Btn kind="primary" sm disabled={busy} onClick={save} testId="api-webhook-save">Save</Btn>
          <Btn sm disabled={busy || !hook.url} onClick={test} testId="api-webhook-test">Send a test</Btn>
          {hook.last_status != null && (
            <St tone={ok(hook.last_status) ? "mint" : "red"} testId="api-webhook-last">
              Last call {hook.last_status || "no answer"} · {ukWhen(hook.last_at)}
            </St>
          )}
        </div>
        {hook.secret && (
          <>
            <Divider />
            <div data-testid="api-webhook-secret">
              <div className="rail-t" style={{ marginBottom: 8 }}>Signing secret</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <div className="copyrow" style={{ flex: 1, minWidth: 200, ...mono }} data-testid="api-webhook-secret-value">
                  {show ? hook.secret : `whsec_${"•".repeat(24)}`}
                </div>
                <Btn sm onClick={() => setShow((s) => !s)} testId="api-webhook-secret-show">{show ? "Hide" : "Show"}</Btn>
                <Btn sm onClick={() => navigator.clipboard.writeText(hook.secret).then(() => toast.success("Copied"))} testId="api-webhook-secret-copy">Copy</Btn>
                <Btn sm disabled={busy} onClick={() => setRotating(true)} testId="api-webhook-secret-rotate">New secret</Btn>
              </div>
            </div>
          </>
        )}
        <Deliveries rows={deliveries} />
      </div>
      <ConfirmDialog open={rotating} onClose={() => setRotating(false)} onYes={rotate} yes="Make a new secret" testId="api-secret-dialog"
                     title="Make a new signing secret?"
                     body="Updates are signed with the new secret straight away. Your system will reject them until you paste it in." />
    </Panel>
  );
};

const Deliveries = ({ rows = [] }) => (rows.length ? (
  <>
    <Divider />
    <div>
      <div className="rail-t" style={{ marginBottom: 8 }}>Last updates sent</div>
      <div style={{ display: "grid", gap: 7 }} data-testid="api-webhook-log">
        {rows.slice(0, 8).map((l) => (
          <div key={l.id} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5, color: "var(--mut)", flexWrap: "wrap" }}>
            <St tone={ok(l.status) ? "mint" : "red"}>{l.status || "no answer"}</St>
            <span style={{ fontWeight: 700, color: "var(--ink)" }}>{l.reference || "—"}</span>
            <span>{l.previous_stage ? `${l.previous_stage} → ` : ""}{l.stage}</span>
            {l.test && <St tone="slate">test</St>}
            {l.attempt > 1 && <span>try {l.attempt}</span>}
            <span style={{ marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>{ukWhen(l.at)}</span>
          </div>
        ))}
      </div>
    </div>
  </>
) : null);
